import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const AdminLoginModal = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('password'); // 'password' | '2fa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setStep('password'); setEmail(''); setPassword(''); setCode(''); setChallenge(''); setError(''); setBusy(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const goToDashboard = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/admin/dashboard');
    }
    handleClose();
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const data = await api.login(email, password);
      if (data.requires_2fa) {
        setChallenge(data.challenge_token);
        setStep('2fa');
      } else {
        goToDashboard();
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await api.login2fa(challenge, code.trim());
      goToDashboard();
    } catch (err) {
      setError(err.message || '2FA failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="admin-login-modal">
      <div className="bg-white rounded-lg w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {step === 'password' ? 'Admin Login' : 'Two-factor code'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700" data-testid="login-close-btn">×</button>
        </div>

        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="login-email-input"
            />
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              data-testid="login-password-input"
            />
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={busy} className="w-full bg-black text-white py-2 rounded text-sm hover:bg-gray-800 disabled:opacity-50" data-testid="login-submit-btn">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}

        {step === '2fa' && (
          <form onSubmit={handle2faSubmit} className="space-y-3">
            <p className="text-sm text-gray-600">Enter the 6-digit code from your authenticator app.</p>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus required
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="w-full border border-gray-300 rounded px-3 py-2 text-center text-lg font-mono tracking-widest"
              data-testid="login-2fa-input"
            />
            {error && <div className="text-sm text-red-600" data-testid="login-2fa-error">{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep('password')} className="px-3 py-2 text-sm border rounded">Back</button>
              <button type="submit" disabled={busy || code.length < 6} className="flex-1 bg-black text-white py-2 rounded text-sm hover:bg-gray-800 disabled:opacity-50" data-testid="login-2fa-submit-btn">
                {busy ? 'Verifying…' : 'Verify & sign in'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLoginModal;
